/**
 * Care Background Agent · frontend/src/components/HandoverReport.tsx
 * Displays Care's end-of-session handover report.
 * Addresses P5.09 — End-of-Session Handover Report
 */

interface Summary {
  task_title?: string;
  files_changed?: string[];
  tests_status?: string;
  pr_url?: string;
  blockers?: string[];
  confidence_level?: "high" | "medium" | "low";
  session_duration_min?: number;
}

interface Props {
  report: string;
  summaryJson?: string;
}

export default function HandoverReport({ report, summaryJson }: Props) {
  let summary: Summary | null = null;
  try {
    if (summaryJson) summary = JSON.parse(summaryJson);
  } catch {}

  const confidenceColor = {
    high:   "text-emerald-400",
    medium: "text-yellow-400",
    low:    "text-red-400",
  }[summary?.confidence_level ?? ""] ?? "text-gray-400";

  const testsColor = {
    all_passing: "text-emerald-400",
    some_failing: "text-yellow-400",
    not_run:     "text-gray-400",
    n_a:         "text-gray-400",
  }[summary?.tests_status ?? ""] ?? "text-gray-400";

  return (
    <div className="bg-[#0f1117] text-sm">
      <div className="sticky top-0 flex items-center gap-2 px-4 py-2 bg-[#181c2a] border-b border-[#2a2e45] z-10">
        <span className="text-purple-400">📋</span>
        <span className="text-white font-medium text-xs">Handover Report</span>
      </div>

      <div className="px-4 py-3 space-y-3">
        {summary && (
          <div className="grid grid-cols-3 gap-2">
            <Pill label="Confidence" value={summary.confidence_level ?? "—"} valueClass={confidenceColor} />
            <Pill
              label="Tests"
              value={(summary.tests_status ?? "—").replace("_", " ")}
              valueClass={testsColor}
            />
            {summary.session_duration_min != null && (
              <Pill label="Duration" value={`${summary.session_duration_min}m`} />
            )}
          </div>
        )}

        {summary?.pr_url && (
          <a
            href={summary.pr_url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2 bg-[#2a2e45] rounded-lg hover:bg-[#3a3e55] transition-colors group"
          >
            <span className="text-sm">🔗</span>
            <span className="text-[#5b4dff] text-xs group-hover:underline truncate">
              {summary.pr_url.replace("https://github.com/", "")}
            </span>
          </a>
        )}

        {summary?.files_changed && summary.files_changed.length > 0 && (
          <div>
            <div className="text-[#8b8fa8] text-xs font-medium mb-1.5">Files Changed</div>
            <div className="space-y-1">
              {summary.files_changed.map((f) => (
                <div key={f} className="flex items-center gap-1.5 text-xs font-mono text-[#c4c8e2]">
                  <span className="text-[#5b4dff]">·</span>
                  {f}
                </div>
              ))}
            </div>
          </div>
        )}

        {summary?.blockers && summary.blockers.length > 0 && (
          <div>
            <div className="text-yellow-400 text-xs font-medium mb-1.5">⚠️ Uncertainty Areas</div>
            <div className="space-y-1">
              {summary.blockers.map((b, i) => (
                <div key={i} className="text-xs text-[#c4c8e2] leading-relaxed">
                  {b}
                </div>
              ))}
            </div>
          </div>
        )}

        <details className="group">
          <summary className="text-[#8b8fa8] text-xs cursor-pointer hover:text-white transition-colors list-none flex items-center gap-1">
            <span className="group-open:rotate-90 transition-transform inline-block">›</span>
            Full report
          </summary>
          <div className="mt-2 pl-3 border-l border-[#2a2e45]">
            <pre className="text-xs text-[#8b8fa8] whitespace-pre-wrap font-sans leading-relaxed">
              {report}
            </pre>
          </div>
        </details>
      </div>
    </div>
  );
}

function Pill({ label, value, valueClass = "text-white" }: { label: string; value: string; valueClass?: string }) {
  return (
    <div className="bg-[#181c2a] border border-[#2a2e45] rounded-lg px-2 py-1.5 text-center">
      <div className="text-[#8b8fa8] text-[10px] uppercase tracking-wider">{label}</div>
      <div className={`text-xs font-semibold mt-0.5 capitalize ${valueClass}`}>{value}</div>
    </div>
  );
}
